'use client';

import { useState } from 'react';
import { Button } from '@myapp/ui/components/button';
import { Badge } from '@myapp/ui/components/badge';
import { Sparkles, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

interface Product {
  id?: number;
  name: string;
  price: string;
  mall?: string;
  originalQuery?: string;
}

interface ProductAnalysisProps {
  product: Product;
  size?: 'sm' | 'md' | 'lg';
}

export function ProductAnalysis({ product, size = 'sm' }: ProductAnalysisProps) {
  const [analysis, setAnalysis] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const handleToggleAnalysis = async () => {
    if (!showAnalysis && !hasLoaded) {
      // Load analysis if not loaded yet
      setLoading(true);
      try {
        const response = await fetch('/api/analyze-individual-product', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ product })
        });
        
        const data = await response.json();
        if (data.analysis) {
          setAnalysis(data.analysis);
          setHasLoaded(true);
        } else {
          setAnalysis('분석 결과를 가져올 수 없습니다.');
        }
      } catch (error) {
        console.error('Analysis error:', error);
        setAnalysis('분석 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    }
    
    setShowAnalysis(!showAnalysis);
  };

  const getButtonSize = () => {
    switch (size) {
      case 'lg': return 'default';
      case 'md': return 'sm';
      case 'sm': 
      default: return 'sm';
    }
  };

  const getTextSize = () => {
    switch (size) {
      case 'lg': return 'text-sm';
      case 'md': return 'text-sm';
      case 'sm': 
      default: return 'text-xs';
    }
  };

  return (
    <div className="mt-2">
      <Button
        variant="outline"
        size={getButtonSize()}
        onClick={handleToggleAnalysis}
        disabled={loading}
        className="w-full"
      >
        {loading ? (
          <>
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            분석중...
          </>
        ) : (
          <>
            <Sparkles className="w-3 h-3 mr-1" />
            AI 분석
            {showAnalysis ? (
              <ChevronUp className="w-3 h-3 ml-1" />
            ) : (
              <ChevronDown className="w-3 h-3 ml-1" />
            )}
          </>
        )}
      </Button>

      {showAnalysis && (
        <div className={`mt-2 p-3`}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-blue-800 text-xs">AI 상품 분석</span>
          </div>
          <div className={`${getTextSize()} text-gray-700 leading-relaxed whitespace-pre-wrap`}>
            {analysis || '분석 결과가 없습니다.'}
          </div>
        </div>
      )}
    </div>
  );
}